/**
 * Spaced Repetition System (SRS) Algorithm
 * Based on SuperMemo SM-2 algorithm with simplifications
 */

// Initial intervals in days
const INITIAL_INTERVALS = {
  again: 1 / 1440,    // 1 minute (for immediate review)
  hard: 1 / 24,       // 1 hour
  medium: 1,          // 1 day
  easy: 4             // 4 days
};

// Interval multipliers based on rating
const MULTIPLIERS = {
  again: 0,           // Reset to beginning
  hard: 1.2,          // Slow growth
  medium: 2.5,        // Standard growth
  easy: 3.0           // Fast growth
};

/**
 * Calculate the next review date for a card
 * @param {Object} cardReview - Current review data
 * @param {string} rating - User rating: 'again', 'hard', 'medium', 'easy'
 * @returns {Object} Updated review data
 */
export const calculateNextReview = (cardReview = null, rating) => {
  const now = Date.now();

  // First review - set initial interval
  if (!cardReview || !cardReview.lastReviewed) {
    const interval = INITIAL_INTERVALS[rating];
    return {
      lastReviewed: now,
      dueDate: now + (interval * 24 * 60 * 60 * 1000),
      interval: interval,
      easeFactor: rating === 'easy' ? 2.6 : rating === 'medium' ? 2.5 : 2.4,
      reviewCount: 1,
      rating: rating
    };
  }

  // Subsequent reviews
  let newInterval;
  let newEaseFactor = cardReview.easeFactor || 2.5;

  if (rating === 'again') {
    // Failed - reset to beginning
    newInterval = INITIAL_INTERVALS.again;
    newEaseFactor = Math.max(1.3, newEaseFactor - 0.2);
  } else {
    // Passed - calculate new interval
    const baseInterval = cardReview.interval || 1;

    if (rating === 'hard') {
      newInterval = baseInterval * MULTIPLIERS.hard;
      newEaseFactor = Math.max(1.3, newEaseFactor - 0.15);
    } else if (rating === 'medium') {
      newInterval = baseInterval * newEaseFactor;
      // Ease factor stays roughly the same for medium
    } else if (rating === 'easy') {
      newInterval = baseInterval * MULTIPLIERS.easy;
      newEaseFactor = Math.min(3.0, newEaseFactor + 0.1);
    }
  }

  return {
    lastReviewed: now,
    dueDate: now + (newInterval * 24 * 60 * 60 * 1000),
    interval: newInterval,
    easeFactor: newEaseFactor,
    reviewCount: (cardReview.reviewCount || 0) + 1,
    rating: rating,
    streak: rating === 'again' ? 0 : (cardReview.streak || 0) + 1
  };
};

/**
 * Check if a card is due for review
 * @param {Object} cardReview - Review data
 * @returns {boolean} True if card is due
 */
export const isCardDue = (cardReview) => {
  if (!cardReview || !cardReview.dueDate) {
    return true; // New cards are always due
  }
  return Date.now() >= cardReview.dueDate;
};

/**
 * Get cards that are due for review
 * @param {Object} reviewData - All review data
 * @param {Array} cardIndices - Card indices to check
 * @returns {Array} Indices of due cards
 */
export const getDueCards = (reviewData, cardIndices) => {
  return cardIndices.filter(index => {
    const review = reviewData[index];
    return isCardDue(review);
  });
};

/**
 * Sort cards by due date (earliest first)
 * @param {Object} reviewData - All review data
 * @param {Array} cardIndices - Card indices to sort
 * @returns {Array} Sorted card indices
 */
export const sortByDueDate = (reviewData, cardIndices) => {
  return [...cardIndices].sort((a, b) => {
    const reviewA = reviewData[a];
    const reviewB = reviewData[b];

    const dueDateA = reviewA?.dueDate || 0;
    const dueDateB = reviewB?.dueDate || 0;

    return dueDateA - dueDateB;
  });
};

/**
 * Get review statistics for a deck
 * @param {Object} reviewData - All review data
 * @returns {Object} Statistics
 */
export const getReviewStats = (reviewData) => {
  const cards = Object.values(reviewData);

  const newCards = cards.filter(r => !r || r.reviewCount === 0).length;
  const learning = cards.filter(r => r && r.reviewCount > 0 && r.interval < 21).length;
  const mature = cards.filter(r => r && r.interval >= 21).length;
  const due = cards.filter(r => isCardDue(r)).length;

  return {
    new: newCards,
    learning,
    mature,
    due,
    total: cards.length
  };
};

/**
 * Format interval for display
 * @param {number} interval - Interval in days
 * @returns {string} Formatted string
 */
export const formatInterval = (interval) => {
  if (interval < 1 / 60) {
    return `${Math.round(interval * 1440)} min`;
  } else if (interval < 1) {
    return `${Math.round(interval * 24)} hrs`;
  } else if (interval < 30) {
    return `${Math.round(interval)} days`;
  } else if (interval < 365) {
    return `${Math.round(interval / 30)} months`;
  } else {
    return `${Math.round(interval / 365)} years`;
  }
};
